/**
 * Intune / device audit Graph calls (C9) — ported from x365 graph.js, behind the
 * graph/ layer. Least-privilege: device + policy reads only (no directory coupling),
 * so the module runs under its own consent.
 */
import { graphBatch, graphGetAll } from '@/graph/client';
import { CAPABILITY_SCOPES } from '@/auth/scopes';

const SCOPES = CAPABILITY_SCOPES.C9.scopes;

export const INTUNE_ENDPOINTS = {
  compliancePolicies: '/deviceManagement/deviceCompliancePolicies',
  configProfiles: '/deviceManagement/deviceConfigurations',
  managedDevices: '/deviceManagement/managedDevices',
  intents: '/deviceManagement/intents',
} as const;

export interface AssignmentTarget {
  '@odata.type'?: string;
  groupId?: string;
}
export interface PolicyAssignment {
  target?: AssignmentTarget;
}

export interface CompliancePolicy {
  id: string;
  displayName?: string | null;
  '@odata.type'?: string;
  passwordRequired?: boolean;
  passwordMinimumLength?: number | null;
  assignments?: PolicyAssignment[];
}

export interface ConfigProfile {
  id: string;
  displayName?: string | null;
  '@odata.type'?: string;
  storageRequireEncryption?: boolean;
  bitLockerEnabled?: boolean;
  omaSettings?: { omaUri?: string; value?: unknown; secretValue?: unknown }[];
  assignments?: PolicyAssignment[];
}

export interface ManagedDevice {
  id: string;
  deviceName?: string | null;
  operatingSystem?: string | null;
  complianceState?: string | null;
  lastSyncDateTime?: string | null;
  userDisplayName?: string | null;
}

export interface EndpointSecurityIntent {
  id: string;
  displayName?: string | null;
}

async function attachAssignments<T extends { id: string }>(
  policies: T[],
  endpoint: string,
): Promise<(T & { assignments: PolicyAssignment[] })[]> {
  if (policies.length === 0) return [];
  const responses = await graphBatch<{ value?: PolicyAssignment[] }>(
    policies.map((p) => ({ id: p.id, method: 'GET', url: `${endpoint}/${p.id}/assignments` })),
    { scopes: SCOPES },
  );
  return policies.map((p) => ({
    ...p,
    assignments: responses.find((r) => r.id === p.id)?.body?.value ?? [],
  }));
}

export async function getCompliancePolicies(): Promise<CompliancePolicy[]> {
  const policies = await graphGetAll<CompliancePolicy>(INTUNE_ENDPOINTS.compliancePolicies, {
    scopes: SCOPES,
  });
  return attachAssignments(policies, INTUNE_ENDPOINTS.compliancePolicies);
}

export async function getConfigProfiles(): Promise<ConfigProfile[]> {
  const profiles = await graphGetAll<ConfigProfile>(INTUNE_ENDPOINTS.configProfiles, {
    scopes: SCOPES,
  });
  return attachAssignments(profiles, INTUNE_ENDPOINTS.configProfiles);
}

export function getManagedDevices(): Promise<ManagedDevice[]> {
  return graphGetAll<ManagedDevice>(INTUNE_ENDPOINTS.managedDevices, {
    scopes: SCOPES,
    query: {
      $select: 'id,deviceName,operatingSystem,osVersion,complianceState,lastSyncDateTime,userId,userDisplayName',
    },
  });
}

export async function getEndpointSecurityPolicies(): Promise<EndpointSecurityIntent[]> {
  try {
    return await graphGetAll<EndpointSecurityIntent>(INTUNE_ENDPOINTS.intents, {
      scopes: SCOPES,
      version: 'beta',
    });
  } catch {
    return [];
  }
}
